import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Heading } from "@astryxdesign/core/Heading";
import { HStack, StackItem } from "@astryxdesign/core/Stack";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Icon } from "@astryxdesign/core/Icon";
import { VStack } from "@astryxdesign/core/VStack";
import { useT } from "@/lib/i18n";

interface ScreenProps {
  title: string;
  /** Show a back button; falls back to `backTo` when there is no history entry. */
  backTo?: string;
  actions?: ReactNode;
  children: ReactNode;
  withNav?: boolean;
}

/**
 * Page frame: safe-area aware header, focus moved to the title on navigation
 * (screen readers announce the new page), content padded above the bottom nav.
 */
export function Screen({ title, backTo, actions, children, withNav = true }: ScreenProps) {
  const navigate = useNavigate();
  const t = useT();
  const headingRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    document.title = title;
    headingRef.current?.focus({ preventScroll: true });
  }, [title]);

  return (
    <VStack as="main" gap={4} paddingInline={4} paddingBlockEnd={4} className={`app-safe-top app-safe-x ${withNav ? "page-with-nav" : ""}`}>
      <HStack as="header" gap={2} vAlign="center" paddingBlockStart={3}>
        {backTo !== undefined ? (
          <IconButton
            label={t.common.back}
            variant="ghost"
            icon={<Icon icon="chevronLeft" />}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate(backTo, { replace: true }))}
          />
        ) : null}
        <StackItem size="fill">
          <span ref={headingRef} tabIndex={-1} className="outline-none">
            <Heading level={1} maxLines={1}>
              {title}
            </Heading>
          </span>
        </StackItem>
        {actions}
      </HStack>
      {children}
    </VStack>
  );
}
